import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, mensajeDe, type TorneoCompleto } from '../../services/api';
import { aProgramado, aRepartir, armarAgenda, nombresDeJugadores, porHorario, type ItemAgenda } from '../../services/agenda';
import { AgendaPorDia, EnCanchaAhora, FilaAgenda } from '../../components/Agenda';
import { Aviso, Boton, Campo, ENTRADA, Tarjeta, Titulo } from '../../components/ui';
import { diaLargo, diasEntre } from '../../nucleo/fechas';
import { choques, diaSiguiente, repartirHorariosEnDias, textoDeHorario, validarHorario, problemaDeCondiciones, type BloqueoDisponibilidad, type JornadaDeReparto, type Reparto } from '../../nucleo/programacion';
import { recursosDePartidos } from '../../nucleo/recursos';
import { aClave } from '../../nucleo/texto';
import { capacidades, OPCIONES_INICIALES, type OpcionesTorneo } from '../../nucleo/opciones';

// Programación: cuándo y dónde se juega cada partido, de todas las categorías
// juntas (las canchas son las mismas para todas). El asistente propone, el
// organizador confirma y después retoca a mano lo que quiera.

/** Cuántos avisos de choque se muestran de una: una lista de 40 no la lee nadie. */
const MAXIMO_DE_AVISOS = 8;

export function Programacion({ datos, recargar }: { datos: TorneoCompleto; recargar: () => Promise<void> }) {
  const { torneo, categorias } = datos;
  const agenda = useMemo(() => armarAgenda(categorias), [categorias]);
  const [editando, setEditando] = useState<number | null>(null);
  const [error, setError] = useState('');
  // Para mirar de a una categoría. Los avisos de choques siempre miran todas.
  const [filtro, setFiltro] = useState<number | null>(null);

  const dias = diasEntre(torneo.fecha_desde, torneo.fecha_hasta);
  const canchas = Array.from({ length: torneo.canchas ?? 0 }, (_, i) => i + 1);
  const porId = new Map(agenda.map((i) => [i.id, i]));

  // Lo que queda por jugar y todavía no tiene horario.
  const aLaVista = filtro === null ? agenda : agenda.filter((i) => i.categoriaId === filtro);
  const sinHorario = aLaVista.filter((i) => !i.jugado && !i.porAbandono && !i.dia).sort(porHorario);
  const hayPorRepartir = agenda.some((i) => !i.jugado && !i.porAbandono && !i.enJuego);
  const avisos = choques(agenda.map(aProgramado));
  const nombres = useMemo(() => nombresDeJugadores(categorias), [categorias]);
  const guardadas = datos.opciones ?? OPCIONES_INICIALES;
  const efectivas = capacidades(guardadas);
  const opciones = {...guardadas, descanso_minutos: efectivas.descansoMinutos};
  const recursos = recursosDePartidos(categorias);
  const bloqueos: BloqueoDisponibilidad[] = (efectivas.disponibilidad ? datos.disponibilidad ?? [] : []).map(d => {
    const p = categorias.flatMap(c => c.parejas).find(p => p.id === d.pareja_id);
    return { parejas: [d.pareja_id], jugadores: p ? [aClave(p.jugador1), aClave(p.jugador2)] : [], restricciones: d.restricciones };
  });
  const avisosCondiciones = agenda.flatMap(i => {
    const r = recursos.get(i.id);
    if (!r || i.jugado || i.porAbandono || !i.dia || !i.hora) return [];
    const problema = problemaDeCondiciones(r, i.dia, i.hora, [...recursos.values()], opciones.duracion_minutos, { bloqueos, descansoMinutos: opciones.descanso_minutos });
    return problema ? [`${textoDeHorario(i)} · ${i.categoria} · ${i.fase}: ${problema}`] : [];
  });
  const diaYHora = (i: ItemAgenda) => textoDeHorario(i).split(' · ').slice(0, 2).join(' · ');

  const accion = async (hacer: () => Promise<unknown>) => {
    setError('');
    try {
      await hacer();
      await recargar();
    } catch (e) {
      setError(mensajeDe(e));
    }
  };

  if (agenda.length === 0) {
    return (
      <Tarjeta>
        <Titulo>Programación</Titulo>
        <p className="text-sm text-(--color-apagado)">Cuando armes los grupos o el cuadro de alguna categoría, acá se programan los partidos.</p>
      </Tarjeta>
    );
  }

  /** Los botones de cada partido: en juego y horario. */
  const acciones = (i: ItemAgenda) => {
    if (i.jugado || i.porAbandono) return null;
    if (i.enJuego) return <button className="min-h-10 text-xs text-(--color-acento)" onClick={() => accion(() => api.enJuego(i.id, false))}>Sacar de “en cancha” para cambiar horario</button>;
    if (editando === i.id) {
      return (
        <EditarHorario
          item={i}
          dias={dias}
          canchas={canchas}
          torneo={torneo}
          alCancelar={() => setEditando(null)}
          alGuardar={async (h) => {
            await api.ponerHorario(i.id, h);
            setEditando(null);
            await recargar();
          }}
        />
      );
    }
    return (
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold">
        <button className="text-(--color-acento)" onClick={() => setEditando(i.id)}>
          {i.dia ? 'Cambiar horario' : 'Poner horario'}
        </button>
        {i.enJuego ? (
          <button className="text-(--color-apagado)" onClick={() => accion(() => api.enJuego(i.id, false))}>
            Sacar de “en cancha”
          </button>
        ) : (
          efectivas.control && i.sePuedeJugar && (
            <button className="min-h-9 rounded-lg border border-(--color-borde) px-2 text-(--color-acento)" onClick={() => accion(() => api.enJuego(i.id, true))}>
              Marcar en juego
            </button>
          )
        )}
      </div>
    );
  };

  return (
    <>
      {torneo.canchas === null && (
        <Aviso tono="alerta">Decí cuántas canchas tiene la sede (arriba, en “Datos”) para poder asignar canchas y repartir horarios.</Aviso>
      )}
      {error && <Aviso tono="error">{error}</Aviso>}
      {avisosCondiciones.length > 0 && <Tarjeta><Titulo>Disponibilidad y descanso para revisar</Titulo><ul className="flex flex-col gap-2 text-sm text-(--color-alerta)">{avisosCondiciones.slice(0, MAXIMO_DE_AVISOS).map((a, k) => <li key={k}>{a}</li>)}</ul><p className="mt-2 text-xs text-(--color-apagado)">Los horarios existentes se conservan. Reprogramá los pendientes para aplicar las condiciones.{avisosCondiciones.length > MAXIMO_DE_AVISOS && ` Hay ${avisosCondiciones.length - MAXIMO_DE_AVISOS} avisos más.`}</p></Tarjeta>}

      {avisos.length > 0 && (
        <Tarjeta className="border-(--color-alerta)">
          <Titulo extra="la app no los impide: te avisa">Para revisar</Titulo>
          <ul className="flex flex-col gap-1.5 text-sm">
            {avisos.slice(0, MAXIMO_DE_AVISOS).map((c, k) => {
              const [a, b] = c.partidos.map((id) => porId.get(id)!) as [ItemAgenda, ItemAgenda];
              return (
                <li key={k} className="text-(--color-alerta)">
                  {c.tipo === 'cancha'
                    ? `${textoDeHorario(a)}: hay dos partidos en la misma cancha (${a.categoria} ${a.fase} y ${b.categoria} ${b.fase}).`
                    : c.tipo === 'pareja'
                      ? `${diaYHora(a)}: una pareja tiene dos partidos a la misma hora (${a.categoria} ${a.fase} y ${b.categoria} ${b.fase}).`
                      : `${diaYHora(a)}: ${nombres.get(c.jugador ?? '') ?? c.jugador} aparece en dos partidos a la misma hora (${a.categoria} ${a.fase} y ${b.categoria} ${b.fase}). Si es la misma persona, cambiá uno.`}
                </li>
              );
            })}
          </ul>
          {avisos.length > MAXIMO_DE_AVISOS && (
            <p className="mt-2 text-sm text-(--color-alerta)">
              Y {avisos.length - MAXIMO_DE_AVISOS} más. A medida que arregles estos, van apareciendo los que siguen.
            </p>
          )}
        </Tarjeta>
      )}

      {efectivas.asistente && hayPorRepartir && canchas.length > 0 && (
        <Asistente torneoId={torneo.id} agenda={agenda} dias={dias} canchas={canchas} categorias={categorias.map((c) => ({ id: c.id, nombre: c.nombre }))} recargar={recargar} opciones={opciones} bloqueos={bloqueos} recursos={recursos} />
      )}

      <EnCanchaAhora items={agenda} extra={acciones} />

      {categorias.length > 1 && (
        <label className="flex items-center gap-2 text-sm text-(--color-apagado)">
          Ver
          <select className={`${ENTRADA} min-h-10 flex-1 text-sm`} value={filtro ?? ''} onChange={(e) => setFiltro(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Todas las categorías</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </label>
      )}

      <AgendaPorDia items={aLaVista} conCategoria={filtro === null} extra={acciones} plegable />

      {sinHorario.length > 0 && (
        <Tarjeta>
          <details className="group/sinhorario">
            <summary className="flex cursor-pointer list-none items-baseline gap-2 font-titulo text-2xl uppercase">
              <span aria-hidden="true" className="text-base text-(--color-apagado) transition-transform group-open/sinhorario:rotate-90">▸</span>
              Sin horario
              <span className="font-sans text-xs normal-case tracking-normal text-(--color-apagado)">{sinHorario.length} por jugar</span>
            </summary>
            <ul className="mt-3 flex flex-col">
              {sinHorario.map((i) => (
                <FilaAgenda key={i.id} item={i} conCategoria={filtro === null}>
                  {acciones(i)}
                </FilaAgenda>
              ))}
            </ul>
          </details>
        </Tarjeta>
      )}
    </>
  );
}

// ── Un horario a mano ─────────────────────────────────────────────────────

function EditarHorario({
  item,
  dias,
  canchas,
  torneo,
  alGuardar,
  alCancelar,
}: {
  item: ItemAgenda;
  dias: string[];
  canchas: number[];
  torneo: { fecha_desde: string; fecha_hasta: string; canchas: number | null };
  alGuardar: (h: { dia: string | null; hora: string | null; cancha: number | null }) => Promise<void>;
  alCancelar: () => void;
}) {
  const [dia, setDia] = useState(item.dia ?? dias[0] ?? '');
  const [hora, setHora] = useState(item.hora ?? '');
  const [cancha, setCancha] = useState(item.cancha?.toString() ?? '');
  const [error, setError] = useState('');

  const mandar = async (cuerpo: unknown) => {
    const h = validarHorario(cuerpo, torneo);
    if (!h.ok) return setError(h.error);
    try {
      await alGuardar(h.valor);
    } catch (e) {
      setError(mensajeDe(e));
    }
  };
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    void mandar({ dia, hora: hora || null, cancha: cancha ? Number(cancha) : null });
  };

  return (
    <form onSubmit={enviar} className="mt-2 flex flex-col gap-2 rounded-xl border border-(--color-acento) p-2.5">
      <div className="grid grid-cols-3 gap-2">
        <select className={`${ENTRADA} px-1.5 text-sm`} value={dia} onChange={(e) => setDia(e.target.value)} aria-label="Día">
          {dias.map((d) => (
            <option key={d} value={d}>{diaLargo(d)}</option>
          ))}
        </select>
        <input className={`${ENTRADA} px-1.5 text-sm`} type="time" value={hora} onChange={(e) => setHora(e.target.value)} aria-label="Hora" />
        <select className={`${ENTRADA} px-1.5 text-sm`} value={cancha} onChange={(e) => setCancha(e.target.value)} aria-label="Cancha">
          <option value="">Sin cancha</option>
          {canchas.map((c) => (
            <option key={c} value={c}>Cancha {c}</option>
          ))}
        </select>
      </div>
      {error && <Aviso tono="error">{error}</Aviso>}
      <div className="flex flex-wrap gap-2">
        <Boton chico type="submit">Guardar</Boton>
        <Boton chico tipo="secundario" onClick={alCancelar}>Cancelar</Boton>
        {item.dia && (
          <Boton chico tipo="texto" onClick={() => mandar({})}>
            Sacarle el horario
          </Boton>
        )}
      </div>
    </form>
  );
}

// ── El asistente ──────────────────────────────────────────────────────────

/**
 * Reparte los partidos que todavía no tienen horario: el organizador dice qué
 * días y ventanas horarias, cuánto dura un turno y en qué canchas; mira la
 * propuesta y la guarda. No toca lo que ya tiene horario.
 */
function Asistente({
  torneoId,
  agenda,
  dias,
  canchas,
  categorias,
  recargar,
  opciones,
  bloqueos,
  recursos,
}: {
  torneoId: number;
  agenda: ItemAgenda[];
  dias: string[];
  canchas: number[];
  categorias: { id: number; nombre: string }[];
  recargar: () => Promise<void>;
  opciones: OpcionesTorneo;
  bloqueos: BloqueoDisponibilidad[];
  recursos: ReturnType<typeof recursosDePartidos>;
}) {
  const [abierto, setAbierto] = useState(false);
  const [modo, setModo] = useState<'sinHorario' | 'reprogramar' | 'quitar'>('sinHorario');
  const [diaOrigen, setDiaOrigen] = useState('');
  const [conservar, setConservar] = useState<number[]>([]);
  const [confirmar, setConfirmar] = useState(false);
  const [jornadas, setJornadas] = useState<JornadaDeReparto[]>(() => dias.map(dia => ({ dia, desde: '18:00', hasta: null })));
  const [diasElegidos, setDiasElegidos] = useState<string[]>(() => dias.slice(0, 1));
  const cada = opciones.duracion_minutos;
  const [usar, setUsar] = useState<number[]>(canchas);
  const [cats, setCats] = useState<number[]>(categorias.map((c) => c.id));
  const [fases, setFases] = useState({ grupos: true, cuadro: true });
  const [propuesta, setPropuesta] = useState<Reparto | null>(null);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {setPropuesta(null);setConfirmar(false);}, [agenda, opciones.duracion_minutos, opciones.descanso_minutos]);
  const porId = new Map(agenda.map((i) => [i.id, i]));
  const elegidos = agenda.filter(
    (i) => !i.jugado && !i.porAbandono && !i.enJuego && (modo !== 'sinHorario' || !i.dia) && (modo !== 'quitar' || !!(i.dia || i.hora || i.cancha)) && (!diaOrigen || i.dia === diaOrigen) && !conservar.includes(i.id) && cats.includes(i.categoriaId) && (i.esCuadro ? fases.cuadro : fases.grupos),
  );

  const cambiar = <T,>(lista: T[], x: T) => (lista.includes(x) ? lista.filter((y) => y !== x) : [...lista, x]);
  const tocar = (hacer: () => void) => {
    hacer();
    setPropuesta(null);
    setConfirmar(false);
  };

  const proponer = () => {
    setError('');
    const seleccionadas = jornadas.filter(j => dias.includes(j.dia) && diasElegidos.includes(j.dia));
    if (seleccionadas.length === 0) return setError('Elegí al menos un día para repartir.');
    if (seleccionadas.some(j => !j.desde)) return setError('Falta desde qué hora en uno de los días elegidos.');
    const fuera = seleccionadas.find(j => j.hasta !== null && j.hasta < j.desde && !dias.includes(diaSiguiente(j.dia)));
    if (fuera) return setError(`La jornada del ${diaLargo(fuera.dia)} continúa el ${diaLargo(diaSiguiente(fuera.dia))}, fuera de las fechas del torneo. Ampliá la fecha de fin en Datos o elegí un último turno antes de medianoche.`);
    if (usar.length === 0) return setError('Elegí al menos una cancha.');
    if (elegidos.length === 0) return setError('Con eso no queda ningún partido para repartir.');
    setPropuesta(
      repartirHorariosEnDias(elegidos.map(i => recursos.get(i.id) ?? aRepartir(i)), agenda.filter(i => !elegidos.some(e => e.id === i.id)).map(i => recursos.get(i.id) ?? aProgramado(i)), {
        jornadas: seleccionadas,
        cadaMinutos: cada,
        canchas: [...usar].sort((a, b) => a - b),
        descansoMinutos: opciones.descanso_minutos,
        bloqueos,
      }),
    );
  };

  const guardar = async () => {
    if (!propuesta) return;
    setError('');
    setEnviando(true);
    try {
      const horarios = [...propuesta.asignados, ...propuesta.sinLugar.map(id => ({id, dia: null, hora: null, cancha: null}))];
      await api.guardarHorarios(torneoId, horarios, 'asistente');
      setPropuesta(null);
      setAbierto(false);
      await recargar();
    } catch (e) {
      setError(mensajeDe(e));
    } finally {
      setEnviando(false);
    }
  };

  if (!abierto) {
    return (
      <div className="flex flex-wrap gap-2">{(['sinHorario', 'reprogramar', 'quitar'] as const).map(m => <Boton key={m} tipo={m === 'sinHorario' ? 'principal' : 'secundario'} onClick={() => {setModo(m);setAbierto(true);setConservar([]);setDiaOrigen('');setConfirmar(false);setError('');}}>{m === 'sinHorario' ? 'Repartir horarios' : m === 'reprogramar' ? 'Reprogramar pendientes' : 'Quitar horarios de pendientes'}</Boton>)}</div>
    );
  }

  const chip = (activo: boolean) =>
    `rounded-full border px-3 py-1 text-xs font-semibold ${
      activo ? 'border-(--color-acento) text-(--color-acento)' : 'border-(--color-borde) text-(--color-apagado)'
    }`;
  const ultima = propuesta?.asignados.at(-1);

  return (
    <Tarjeta className="border-(--color-acento)">
      <Titulo>{modo === 'quitar' ? 'Quitar horarios de pendientes' : modo === 'reprogramar' ? 'Reprogramar pendientes' : 'Repartir horarios'}</Titulo>
      <p className="mb-3 text-sm text-(--color-apagado)">Los partidos en juego y con resultado se conservan. Elegí qué categorías y partidos querés cambiar.</p>
      <div className="flex flex-col gap-3">
        <Campo etiqueta="Horarios actuales a modificar">
          <select aria-label="Día de horarios actuales" className={ENTRADA} value={diaOrigen} onChange={e => tocar(() => setDiaOrigen(e.target.value))}><option value="">Todos los días</option>{dias.map(d => <option key={d} value={d}>{diaLargo(d)}</option>)}</select>
        </Campo>
        {modo !== 'quitar' && <>
        <Campo etiqueta="Días para repartir" ayuda="Elegí uno o varios. Cada jornada puede tener su propio horario; se reparten los partidos en orden de fecha.">
          <div className="flex flex-wrap gap-2">
            {dias.map(d => <button key={d} type="button" aria-pressed={diasElegidos.includes(d)} className={chip(diasElegidos.includes(d))} onClick={() => tocar(() => {
              setDiasElegidos(cambiar(diasElegidos, d));
              setJornadas(js => js.some(j => j.dia === d) ? js : [...js, { dia: d, desde: '18:00', hasta: null }]);
            })}>{diaLargo(d)}</button>)}
          </div>
        </Campo>
        {jornadas.filter(j => dias.includes(j.dia) && diasElegidos.includes(j.dia)).map(j => (
          <div key={j.dia} className="rounded-xl border border-(--color-borde) p-3">
            <h3 className="mb-2 text-sm font-semibold">{diaLargo(j.dia)}</h3>
            <div className="grid grid-cols-2 gap-3">
              <Campo etiqueta="Desde">
                <input className={ENTRADA} type="time" aria-label={`Desde ${j.dia}`} value={j.desde} onChange={e => tocar(() => setJornadas(js => js.map(x => x.dia === j.dia ? {...x, desde: e.target.value} : x)))} />
              </Campo>
              <Campo etiqueta="Último turno">
                <input className={ENTRADA} type="time" aria-label={`Último turno ${j.dia}`} value={j.hasta ?? ''} onChange={e => tocar(() => setJornadas(js => js.map(x => x.dia === j.dia ? {...x, hasta: e.target.value || null} : x)))} />
              </Campo>
            </div>
            <p className="mt-2 text-xs text-(--color-apagado)">{j.hasta !== null && j.hasta < j.desde ? `Continúa después de medianoche: último partido el ${diaLargo(diaSiguiente(j.dia))} a las ${j.hasta}.` : 'Último turno es la hora de inicio del último partido. Vacío: hasta el final de ese día.'}</p>
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Un turno cada">
            <p className={`${ENTRADA} text-sm`}>{cada} minutos</p>
          </Campo>
        </div>

        <p className="text-xs text-(--color-apagado)">Descanso mínimo: {opciones.descanso_minutos ? `${opciones.descanso_minutos} minutos obligatorios` : 'desactivado; se permite jugar seguido'}. La duración y el descanso se guardan en “Disponibilidad y descanso”. Con participantes sin definir, se consideran todas las parejas que podrían llegar a ese cruce.</p>

        <Campo etiqueta="Canchas">
          <div className="flex flex-wrap gap-2">
            {canchas.map((c) => (
              <button key={c} type="button" aria-pressed={usar.includes(c)} className={chip(usar.includes(c))} onClick={() => tocar(() => setUsar(cambiar(usar, c)))}>
                Cancha {c}
              </button>
            ))}
          </div>
        </Campo>

        </>}
        <Campo etiqueta="Qué partidos">
          <div className="flex flex-wrap gap-2">
            {categorias.map((c) => (
              <button key={c.id} type="button" aria-pressed={cats.includes(c.id)} className={chip(cats.includes(c.id))} onClick={() => tocar(() => setCats(cambiar(cats, c.id)))}>
                {c.nombre}
              </button>
            ))}
          </div>
          <div className="mt-1 flex flex-wrap gap-2">
            <button type="button" aria-pressed={fases.grupos} className={chip(fases.grupos)} onClick={() => tocar(() => setFases({ ...fases, grupos: !fases.grupos }))}>
              Grupos
            </button>
            <button type="button" aria-pressed={fases.cuadro} className={chip(fases.cuadro)} onClick={() => tocar(() => setFases({ ...fases, cuadro: !fases.cuadro }))}>
              Cuadro
            </button>
          </div>
        </Campo>

        {modo !== 'sinHorario' && <details className="rounded-xl border border-(--color-borde) p-3"><summary className="cursor-pointer text-sm font-semibold">Conservar horarios elegidos ({conservar.length})</summary><p className="my-2 text-xs text-(--color-apagado)">Marcá los partidos cuyo horario querés mantener.</p><div className="max-h-64 overflow-y-auto">{agenda.filter(i => !i.jugado && !i.porAbandono && !i.enJuego && i.dia && cats.includes(i.categoriaId) && (!diaOrigen || i.dia === diaOrigen) && (i.esCuadro ? fases.cuadro : fases.grupos)).map(i => <label key={i.id} className="flex min-h-12 items-start gap-2 border-b border-(--color-borde) py-2 text-xs"><input type="checkbox" aria-label={`Conservar horario del partido ${i.id}`} checked={conservar.includes(i.id)} onChange={() => tocar(() => setConservar(cambiar(conservar, i.id)))} /><span>{textoDeHorario(i)} · {i.categoria} · {i.fase}<br />{i.a.texto} – {i.b.texto}</span></label>)}</div></details>}
        <p className="text-xs text-(--color-apagado)">
          {elegidos.length} partido{elegidos.length === 1 ? '' : 's'} para modificar. {modo !== 'quitar' && <>Nadie juega dos partidos a la vez, se respeta la disponibilidad y el descanso configurado, y el cuadro va después de los grupos. Tampoco pone a la misma persona en dos partidos a la vez si juega dos categorías
          (lo mira por el nombre).</>}
        </p>

        {error && <Aviso tono="error">{error}</Aviso>}

        {propuesta && (
          <div className="flex flex-col gap-2 text-sm">
            <p className="font-semibold text-(--color-acento)">
              {propuesta.asignados.length} partido{propuesta.asignados.length === 1 ? '' : 's'}
              {ultima ? `, del ${diaLargo(propuesta.asignados[0]!.dia)} ${propuesta.asignados[0]!.hora} al ${diaLargo(ultima.dia)} ${ultima.hora}` : ''}.
            </p>
            {propuesta.sinLugar.length > 0 && (
              <p className="text-(--color-alerta)">
                {propuesta.sinLugar.length} quedarán sin horario al guardar (se quitará el anterior si tenían): pueden faltar turnos, disponibilidad, descanso o partidos previos por programar. Agregá días o canchas, revisá las restricciones o repartí primero los grupos.
              </p>
            )}
            <ul className="max-h-64 overflow-y-auto rounded-xl border border-(--color-borde) px-2.5 py-1 text-xs">
              {propuesta.asignados.map((a) => {
                const i = porId.get(a.id)!;
                return (
                  <li key={a.id} className="flex gap-2 border-t border-(--color-borde) py-1 first:border-t-0">
                    <span className="w-28 shrink-0 font-semibold tabular-nums">{diaLargo(a.dia)}<br />{a.hora} · C{a.cancha}</span>
                    <span className="min-w-0 flex-1 text-(--color-apagado)">
                      {i.a.texto} – {i.b.texto} <span className="opacity-70">({i.categoria} · {i.fase})</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {modo === 'quitar' && confirmar && <Aviso tono="alerta">Se quitarán día, hora y cancha de {elegidos.length} partidos. Los partidos conservados, en juego y con resultado no se modifican.</Aviso>}
        <div className="flex flex-wrap gap-2">
          {modo === 'quitar' ? <Boton chico tipo="peligro" deshabilitado={enviando || elegidos.length === 0} onClick={async () => {
            if (!confirmar) {setConfirmar(true);return;}
            setEnviando(true);setError('');
            try {await api.guardarHorarios(torneoId, elegidos.map(i => ({id:i.id,dia:null,hora:null,cancha:null})));setAbierto(false);setConfirmar(false);await recargar();} catch(e) {setError(mensajeDe(e));} finally {setEnviando(false);}
          }}>{enviando ? 'Quitando...' : confirmar ? 'Confirmar quitar horarios' : 'Quitar estos horarios'}</Boton> : propuesta && (propuesta.asignados.length > 0 || propuesta.sinLugar.length > 0) ? (
            <Boton chico onClick={guardar} deshabilitado={enviando}>
              {enviando ? 'Guardando...' : 'Guardar estos horarios'}
            </Boton>
          ) : (
            <Boton chico onClick={proponer}>Proponer</Boton>
          )}
          <Boton chico tipo="secundario" onClick={() => { setAbierto(false); setPropuesta(null); }}>Cerrar</Boton>
        </div>
      </div>
    </Tarjeta>
  );
}
